import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  DataTable,
  Spinner,
  Icon,
} from '@shopify/polaris';
import {
  InventoryIcon,
  OrderIcon,
  DeliveryIcon,
  ChartVerticalIcon,
  ClockIcon,
  PersonIcon,
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface ReportExport {
  id: number;
  report_type: string;
  report_name: string;
  row_count: number;
  file_size_bytes: number;
  generated_by_name: string;
  generated_at: string;
  download_count: number;
}

export default function Reports() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [exports, setExports] = useState<ReportExport[]>([]);

  const loadExports = useCallback(async () => {
    const data = await fetchApi<{ exports: ReportExport[] }>('/reports/exports?limit=10');
    if (data) {
      setExports(data.exports || []);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getReportBadge = (type: string) => {
    const config: Record<string, { tone: 'info' | 'success' | 'warning' | 'attention'; label: string }> = {
      backorder: { tone: 'warning', label: 'Backorder' },
      product_flow: { tone: 'info', label: 'Product Flow' },
      stock_valuation: { tone: 'success', label: 'Stock Valuation' },
      delivery_performance: { tone: 'attention', label: 'Delivery' },
      best_sellers: { tone: 'success', label: 'Best Sellers' },
      slow_moving: { tone: 'warning', label: 'Slow Moving' },
      picker_productivity: { tone: 'info', label: 'Picker Productivity' },
    };
    const c = config[type] || { tone: 'info', label: type };
    return <Badge tone={c.tone}>{c.label}</Badge>;
  };

  const handleDownload = async (exportId: number) => {
    window.open(`${import.meta.env.VITE_API_URL}/api/reports/exports/${exportId}/download`, '_blank');
  };

  const reportCards = [
    {
      title: 'Backorder Report',
      description: 'View all products with backordered quantities across orders. Identify stock shortages and prioritize replenishment.',
      icon: OrderIcon,
      path: '/reports/backorder',
      tone: 'warning' as const,
    },
    {
      title: 'Product Flow Report',
      description: 'Analyze stock movements over time. Track receives, dispatches, adjustments, and transfers by product.',
      icon: InventoryIcon,
      path: '/reports/product-flow',
      tone: 'info' as const,
    },
    {
      title: 'Stock Valuation',
      description: 'View current stock value by warehouse and location. Calculate total inventory value at cost.',
      icon: InventoryIcon,
      path: '/reports/stock-valuation',
      tone: 'success' as const,
    },
    {
      title: 'Delivery Performance',
      description: 'Track delivery success rates, failed deliveries, and route performance metrics.',
      icon: DeliveryIcon,
      path: '/reports/delivery-performance',
      tone: 'attention' as const,
    },
    {
      title: 'Best Sellers',
      description: 'View top performing products by sales volume and revenue. Identify bestsellers and stock accordingly.',
      icon: ChartVerticalIcon,
      path: '/reports/best-sellers',
      tone: 'success' as const,
    },
    {
      title: 'Slow Moving Stock',
      description: 'Identify products with low turnover rates. Find items with no sales, overstock, or slow movement.',
      icon: ClockIcon,
      path: '/reports/slow-moving',
      tone: 'warning' as const,
    },
    {
      title: 'Picker Productivity',
      description: 'Track warehouse picker performance including items per hour, accuracy rates, and daily metrics.',
      icon: PersonIcon,
      path: '/reports/picker-productivity',
      tone: 'info' as const,
    },
  ];

  const exportRows = exports.map((exp) => [
    getReportBadge(exp.report_type),
    exp.report_name || exp.report_type,
    String(exp.row_count),
    formatBytes(exp.file_size_bytes),
    exp.generated_by_name,
    formatDate(exp.generated_at),
    <Button size="slim" onClick={() => handleDownload(exp.id)}>
      Download
    </Button>,
  ]);

  return (
    <Page
      title="Reports"
      subtitle="Generate and export reports for your business"
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Report Cards */}
        <Layout.Section>
          <Text as="h2" variant="headingMd">Available Reports</Text>
        </Layout.Section>

        <Layout.Section>
          <InlineStack gap="400" wrap>
            {reportCards.map((report) => (
              <div key={report.path} style={{ width: '280px', maxWidth: '100%' }}>
                <Card>
                  <div style={{ minHeight: 210, display: 'grid', gridTemplateRows: 'auto 1fr auto', gap: 12 }}>
                    <InlineStack gap="200" align="start">
                      <div style={{
                        padding: '8px',
                        borderRadius: '8px',
                        backgroundColor: report.tone === 'warning' ? '#FFF3CD' :
                                        report.tone === 'info' ? '#D1ECF1' :
                                        report.tone === 'success' ? '#D4EDDA' : '#E7E5FF',
                      }}>
                        <Icon source={report.icon} />
                      </div>
                      <Text as="h3" variant="headingSm">{report.title}</Text>
                    </InlineStack>

                    <Text as="p" variant="bodySm" tone="subdued">
                      {report.description}
                    </Text>

                    <Button onClick={() => navigate(report.path)}>
                      View Report
                    </Button>
                  </div>
                </Card>
              </div>
            ))}
          </InlineStack>
        </Layout.Section>

        {/* Recent Exports */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Recent Exports</Text>
                <Button variant="plain" onClick={loadExports}>Refresh</Button>
              </InlineStack>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : exports.length === 0 ? (
                <Banner tone="info">
                  No exports yet. Generate a report and export it to CSV to see it here.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
                  headings={['Type', 'Name', 'Rows', 'Size', 'Generated By', 'Date', 'Actions']}
                  rows={exportRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
